import React, { useCallback, useMemo, useRef, useState, memo, useEffect } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { LocationFetch } from '../../../library/LocationFetch';
import { IfscFetch } from '../../../library/IfscFetch';
import { showErrorSnackbar, showWarningSnackbar } from '../../../components/snackBar/snakbar';

const ADDRESS_MAX_LENGTH = 500;
const isAddressFieldName = (fieldName) =>
  fieldName === 'billingAddress' || fieldName === 'billingAddressD' || fieldName === 'address' || fieldName === 'buisnessAddress';

const FormikTextField = memo(
  React.forwardRef(function FormikTextField(
    {
      formik,
      name,
      label,
      rows,
      resetbool, // kept for API compatibility (not used without effects)
      cityref,
      disabled,
      required,
      placeholder,
      handlePan, // kept for compatibility
      district = false,
      id,
      viewPage,
      states = [],
      setGstApi = () => {},
      upiChange = () => {},
      maxLength = 50,
      setOptions = () => {},
      /** When true and the field is disabled (or view-only), do not show required * / error styling from stale Formik errors. Opt-in only; default leaves behavior unchanged. */
      hideValidationChromeWhenDisabled = false,
    },
    ref
  ) {
    // ---- Local editing buffer (no effects) ----
    const [value, setValue] = useState(formik.values?.[name] ?? '');
    const [editing, setEditing] = useState(false);
    const [paste, setPaste] = useState(false);

    // Debounce timer (no effects)
    const debounceRef = useRef(null);
    useEffect(() => {
      setValue('');
    }, [resetbool]);
    const scheduleSet = useCallback((fn, delay = 50) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        fn();

        debounceRef.current = null;
      }, delay);
    }, []);

    // Displayed value comes from local while editing, otherwise from Formik
    const baseDisplayValue = editing ? value : (formik.values?.[name] ?? '');
    const displayValue = baseDisplayValue;

    const setTouched = useCallback(() => {
      formik.setFieldTouched(name, true, false);
    }, [name]);

    const handleChange = useCallback(
      (event) => {
        const raw = event.target.value;
        formik.setFieldTouched(name, true);
        // PAN
        if (name === 'pan') {
          const up = raw.toUpperCase().trim();
          setEditing(true);
          setValue(up);
          if (raw.length === 10 || raw.length === 9 || raw.length < 2) {
            formik.setFieldValue(name, up);
            setTouched();
            formik.validateField(name);
          }
          return;
        }
        console.log(name);
        if (name === 'legalName') {
          const input = event.target;
          const cursor = input.selectionStart;

          const raw = input.value.slice(0, 50);

          const formatted = raw.replace(/\b\w/g, (l) => l.toUpperCase());

          setEditing(true);
          setValue(formatted);
          formik.setFieldValue('legalShortName', formatted);
          formik.setFieldValue('legalName', formatted);
          formik.setFieldTouched('legalShortName', false);
          requestAnimationFrame(() => {
            input.setSelectionRange(cursor, cursor);
          });
          return;
        }
        if (name === 'legalShortName') {
          const input = event.target;
          const cursor = input.selectionStart;

          const raw = input.value.slice(0, 50);

          const formatted = raw.replace(/\b\w/g, (l) => l.toUpperCase());
          setEditing(true);
          setValue(formatted);
          formik.setFieldValue('legalShortName', formatted);
          // formik.setFieldValue('legalName', formatted);
          formik.setFieldTouched('legalShortName', false);
          setTimeout(() => {
            if (document.activeElement === input && cursor !== null) {
              input.setSelectionRange(cursor, cursor);
            }
          }, 0);
          return;
        }
        if (name === 'ConsigneeName' || name === 'fullname' || name === 'Designation') {
          const input = event.target;
          const cursor = input.selectionStart;

          const raw = input.value.slice(0, 50);

          const formatted = raw.replace(/\b\w/g, (l) => l.toUpperCase());
          setEditing(true);
          setValue(formatted);
          formik.setFieldValue(name, formatted);
          formik.setFieldTouched(name, false);
          requestAnimationFrame(() => {
            input.setSelectionRange(cursor, cursor);
          });
          return;
        }
        // Foreign state and district - capitalize first letter of each word
        if (name === 'foreignstate' || name === 'foreigndistrict' || name === 'foreignstateD' || name === 'foreigndistrictD') {
          const input = event.target;
          const cursor = input.selectionStart;

          const raw = input.value.slice(0, 50);

          const formatted = raw.replace(/\b\w/g, (l) => l.toUpperCase());
          setEditing(true);
          setValue(formatted);
          if (raw.length <= 1) {
            formik.setFieldValue(name, formatted);
          } else {
            scheduleSet(() => formik.setFieldValue(name, formatted));
          }
          requestAnimationFrame(() => {
            input.setSelectionRange(cursor, cursor);
          });
          return;
        }
        // GSTIN / ConsigneeGSTIN
        if (name === 'gstin' || name === 'ConsigneeGSTIN') {
          const up = raw.toUpperCase().trim();
          setEditing(true);
          setValue(up);
          if (name === 'gstin' && up.length === 15) setGstApi(true);

          if (up.length === 15) {
            formik.setFieldValue(name, up);
            setTouched();
            formik.validateField(name);
          }
          return;
        }

        // Email
        if (name === 'email' || name === 'EmailContact') {
          const v = raw.trim();
          setEditing(true);
          setValue(v);
          formik.setFieldTouched(name, false);
          if (v.length < 2 || (v.includes('@') && v.includes('.'))) {
            scheduleSet(() => formik.setFieldValue(name, v));
          }
          return;
        }

        // Multiline / notes
        if (rows) {
          const input = event.target;
          const cursor = input.selectionStart;

          let raw = input.value;
          const isAddressField = isAddressFieldName(name);
          const rowMaxLength = isAddressField ? ADDRESS_MAX_LENGTH : name === 'Comments' ? 250 : maxLength;

          if (raw.length > rowMaxLength) {
            raw = raw.slice(0, rowMaxLength);
            if (name === 'Comments') {
              showWarningSnackbar('Comments cannot exceed 250 characters. Extra text has been removed.');
            } else if (isAddressField) {
              showWarningSnackbar('Address details cannot exceed 500 characters. Extra text has been removed.');
            }
          }

          // Capitalize first letter of each word
          const formatted = raw.replace(/\b\w/g, (l) => l.toUpperCase());

          // Special handling for address fields (optional, same logic really)
          const capitalized =
            name === 'billingAddress' || name === 'billingAddressD' ? (raw ? raw.replace(/\b\w/g, (l) => l.toUpperCase()) : '') : raw;

          setEditing(true);
          setValue(formatted);

          if (raw.length <= 1) {
            formik.setFieldValue(name, formatted);
          } else {
            scheduleSet(() => formik.setFieldValue(name, formatted));
          }

          requestAnimationFrame(() => {
            input.setSelectionRange(cursor, cursor);
          });

          return;
        }

        // Default
        if (raw.length <= 100) {
          setEditing(true);
          setValue(raw);

          if (name === 'upi') {
            upiChange(raw);
            formik.setFieldValue(name, raw); // immediate write for UPI
            return;
          }

          if (paste === true || name === 'bankAC' || raw.length <= 1) {
            formik.setFieldValue(name, raw);
          } else {
            scheduleSet(() => formik.setFieldValue(name, raw));
          }
        }
      },
      [name, rows, paste, setGstApi, upiChange, scheduleSet, setTouched]
    );

    const handleIfsc = useCallback(
      (e) => {
        const val = e.target.value;
        if (val.length <= 25) {
          const up = val.toUpperCase().trim();
          setEditing(true);
          setValue(val);

          formik.setFieldValue(name, up);
          setTouched();
          formik.validateField(name);

          if (up.length === 11) {
            IfscFetch(up)
              .then((res) => {
                formik.setValues({
                  ...formik.values,
                  ifsc: up,
                  branch: res.data.branchName,
                  bankName: res.data.bankName,
                  swiftcode: res.data.swift,
                });
              })
              .catch(() => {
                showErrorSnackbar('IFSC not found in our database');
              });
          }
        }
      },
      [formik, name, setTouched]
    );

    const handleKeyDown = useCallback(
      (e, fieldName) => {
        if (e.key === 'Tab' || e.key === 'Enter') {
          const fieldError = formik.errors?.[fieldName];
          const fieldTouched = formik.touched?.[fieldName];

          if (fieldError && fieldTouched) {
            e.preventDefault();
            e.stopPropagation();
          }
        }
      },
      [formik.errors, formik.touched]
    );

    const inputPropsMemo = useMemo(() => {
      if (name === 'billingAddress' || name === 'billingAddressD') {
        return {
          style: { alignItems: 'flex-start', letterSpacing: 'normal' },
          classes: { input: 'custom-scrollbar' },
        };
      }
      return { classes: { input: 'custom-scrollbar' } };
    }, [name]);

    const inputLabelPropsMemo = useMemo(() => {
      if (name === 'notes') return { shrink: Boolean(displayValue) };
      if (name === 'upi') return { shrink: true };
      return undefined;
    }, [name, displayValue]);

    const onBlur = useCallback(() => {
      // Flush pending change on blur and stop editing
      if ((formik.values?.[name] ?? '') !== value) {
        formik.setFieldValue(name, value, false);
      }
      // setTouched();
      setEditing(false);
      setPaste(false);
      formik.setFieldTouched(name, true, false);
      if (name === 'gstin' || name === 'ConsigneeGSTIN') {
        setTimeout(() => {
          formik.validateField(name);
        }, 0);
      }
    }, [formik, name, value]);

    const onFocus = useCallback(() => {
      setEditing(true);
      const fv = formik.values?.[name] ?? '';
      setValue(fv === 0 || fv === '0' ? '' : fv);
    }, [formik, name]);
    const suppressErrorChrome = hideValidationChromeWhenDisabled && (viewPage || disabled);
    const isMandatory = !suppressErrorChrome && (required || Boolean(formik.errors?.[name]));
    const showAddressCharCount = isAddressFieldName(name) && rows;
    return (
      <>
        {showAddressCharCount ? (
          <Box sx={{ textAlign: 'right', fontSize: '10px' }}>
            <span>
              {displayValue?.length ?? 0}/{ADDRESS_MAX_LENGTH}
            </span>
          </Box>
        ) : null}
        <TextField
          size="small"
          id={name}
          required={isMandatory}
          name={name}
          value={displayValue}
          fullWidth
          placeholder={placeholder}
          error={Boolean(
            !suppressErrorChrome && formik.touched?.[name] && formik.errors?.[name] && !formik.values?.[name]
          )}
          label={label}
          InputLabelProps={inputLabelPropsMemo}
          sx={
            isMandatory
              ? {
                  '.MuiFormLabel-asterisk': {
                    color: 'danger.main',
                    fontSize: '20px',
                    fontWeight: 'bold',
                  },
                }
              : undefined
          }
          // sx={{
          //   fontFamily: 'overpass',
          //   '.MuiFormLabel-asterisk': {
          //     color: 'danger.main',
          //     fontSize: '20px',
          //     fontWeight: 'bold',
          //   },
          // }}
          // required={Boolean(formik.errors?.[name] && !formik.values?.[name])}
          disabled={viewPage || disabled}
          onFocus={onFocus}
          onBlur={onBlur}
          multiline={Boolean(rows && rows > 2)}
          rows={rows || undefined}
          inputRef={ref}
          autoComplete="off"
          onKeyDown={(e) => handleKeyDown(e, name)}
          onChange={
            name === 'ifsc'
              ? handleIfsc
              : // : name === 'Pincode'
                // ? (e) => handlePincode(e, 'State', 'District', 'Country')
                // : name === 'PincodeD'
                // ? (e) => handlePincode(e, 'StateD', 'DistrictD', 'CountryD')
                handleChange
          }
          onPaste={() => setPaste(true)}
          InputProps={inputPropsMemo}
          inputProps={{
            maxLength: isAddressFieldName(name) ? ADDRESS_MAX_LENGTH + 1 : maxLength,
          }}
        />

        {!suppressErrorChrome && formik.touched?.[name] && formik.errors?.[name] ? (
          <Typography variant="body1" className="error" style={{ fontSize: 12, padding: 0 }}>
            {formik.errors[name]}
          </Typography>
        ) : (
          <p style={{ padding: 9 }} />
        )}
      </>
    );
  })
);

export default FormikTextField;
